import React from 'react';
import { Box, Group, Kbd, Text, rem } from '@mantine/core';
import { Spotlight } from '@mantine/spotlight';
import { spotlightHandle, spotlightStore } from '@/lib/spotlight/spotlight.store';
import { IconSearch } from '@tabler/icons-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { navigationFindById, navigationResolve } from '@/function/navigation/navigation.find';
import navigationEntities from '@/function/navigation/navigation.entities';
import navigationSearch from '@/function/navigation/navigation.search';
import { useCustomers } from '@/hooks/use.customers';
import { useInvoices } from '@/hooks/use.invoices';
import { useQuotes } from '@/hooks/use.quotes';
import { useAllTasks, useProjects } from '@/pages/work/config/work.store';
import { useAppSelector } from '@/store/store.hooks';
import { selectRecentPaths } from '@/store/ui/ui.selectors';
import { LEVEL_SLIDE } from '@/theme/motion';
import { useIsPhone, useIsTouch } from '@/hooks/use.responsive';
import type { TypeNavNode } from '@/types/ui/types.ui.navigation';
import { NAVIGATION_TREE, ROOT_NODES } from './config/navigation.tree';
import SpotlightLevel from './spotlight/Spotlight.Level';
import SpotlightPath from './spotlight/Spotlight.Path';
import SpotlightResults from './spotlight/Spotlight.Results';

const Hint: React.FC<{ keys: string[]; label: string }> = ({ keys, label }) => (
    <Group gap={4} wrap="nowrap">
        {keys.map((key) => <Kbd key={key} size="xs">{key}</Kbd>)}
        <Text size="xs" c="dimmed" ml={2}>{label}</Text>
    </Group>
);

const ShellSpotlight: React.FC = () => {
    const navigate = useNavigate();
    const reduced = useReducedMotion();
    const recentPaths = useAppSelector(selectRecentPaths);
    const isPhone = useIsPhone();
    const isTouch = useIsTouch();

    const customers = useCustomers();
    const projects = useProjects();
    const tasks = useAllTasks();
    const quotes = useQuotes();
    const invoices = useInvoices();

    const entities = React.useMemo(
        () => navigationEntities({ customers, projects, tasks, quotes, invoices }),
        [customers, projects, tasks, quotes, invoices],
    );

    const [query, setQuery] = React.useState('');
    const [levelId, setLevelId] = React.useState<string | null>(null);
    const [direction, setDirection] = React.useState<1 | -1>(1);

    const level = React.useMemo(() => (levelId ? navigationFindById(ROOT_NODES, levelId) : null), [levelId]);
    const items = level ? (level.node.children ?? []) : NAVIGATION_TREE;
    const hits = React.useMemo(() => navigationSearch(ROOT_NODES, entities, query), [entities, query]);

    const recent = React.useMemo(() => {
        const seen = new Set<string>();
        return recentPaths
            .map((path) => navigationResolve(ROOT_NODES, path)?.node)
            .filter((node): node is TypeNavNode => Boolean(node) && !seen.has(node!.id) && Boolean(seen.add(node!.id)))
            .slice(0, 3);
    }, [recentPaths]);

    const enter = (node: TypeNavNode) => {
        setDirection(1);
        setQuery('');
        setLevelId(node.id);
    };

    const up = () => {
        if (!level) return;
        setDirection(-1);
        setLevelId(level.trail.length > 1 ? level.trail[level.trail.length - 2].id : null);
    };

    const open = (path: string) => {
        navigate(path);
        spotlightHandle.close();
    };

    const reset = () => {
        setQuery('');
        setLevelId(null);
        setDirection(1);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const caretAtEnd = input.selectionStart === input.value.length;
        const caretAtStart = input.selectionStart === 0 && input.selectionEnd === 0;

        if (event.key === 'ArrowRight' && caretAtEnd) {
            const selected = document.querySelector<HTMLElement>('[data-action][data-selected]');
            const branchId = selected?.dataset.branchId;
            if (branchId) {
                event.preventDefault();
                const target = navigationFindById(ROOT_NODES, branchId);
                if (target) enter(target.node);
            }
            return;
        }
        if ((event.key === 'ArrowLeft' && caretAtStart) || (event.key === 'Backspace' && query === '')) {
            if (level) {
                event.preventDefault();
                up();
            }
        }
    };

    const searching = query.trim().length > 0;
    const panelKey = searching ? 'search' : (levelId ?? 'root');

    return (
        <Spotlight.Root
            store={spotlightStore}
            query={query}
            onQueryChange={setQuery}
            onSpotlightClose={reset}
            closeOnActionTrigger={false}
            clearQueryOnClose
            shortcut={['mod + K']}
            radius="lg"
            size={rem(640)}
            yOffset="12vh"
            fullScreen={isPhone}
            styles={isPhone ? {
                content: { display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' },
                body: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
            } : undefined}
            overlayProps={{ backgroundOpacity: 0.55, blur: 4 }}
            transitionProps={{ transition: 'fade-down', duration: reduced ? 0 : 180 }}
        >
            <Spotlight.Search
                placeholder={level ? `Cerca in ${level.node.label} o in tutto…` : 'Cerca o vai a…'}
                leftSection={<IconSearch size={20} stroke={1.7} />}
                onKeyDown={onKeyDown}
                aria-label="Cerca pagine ed entità"
            />

            <AnimatePresence initial={false}>
                {level && !searching ? <SpotlightPath key="path" trail={level.trail} onBack={up} /> : null}
            </AnimatePresence>

            <Spotlight.ActionsList mah={isPhone ? undefined : rem(420)} style={{ overflowX: 'hidden', ...(isPhone ? { flex: 1, minHeight: 0 } : {}) }}>
                <AnimatePresence initial={false} mode="wait" custom={direction}>
                    <motion.div
                        key={panelKey}
                        custom={direction}
                        variants={reduced ? undefined : LEVEL_SLIDE}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        {searching ? (
                            <SpotlightResults query={query.trim()} hits={hits} onEnter={enter} onOpen={open} />
                        ) : (
                            <SpotlightLevel
                                level={level?.node ?? null}
                                items={items}
                                recent={recent}
                                onEnter={enter}
                                onOpen={(node) => open(node.path)}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </Spotlight.ActionsList>

            {isTouch ? null : (
                <Spotlight.Footer>
                    <Box style={{ display: 'flex', flexWrap: 'wrap', gap: rem(14), justifyContent: 'center', width: '100%' }}>
                        <Hint keys={['↑', '↓']} label="muovi" />
                        <Hint keys={['→']} label="entra" />
                        <Hint keys={['←']} label="risali" />
                        <Hint keys={['↵']} label="apri" />
                        <Hint keys={['esc']} label="chiudi" />
                    </Box>
                </Spotlight.Footer>
            )}
        </Spotlight.Root>
    );
};

export default ShellSpotlight;
